import {
  capitalizeFormat,
  clearStringData,
  checkValidationRequiredString,
  Enum_ValidationString,
  checkNumCharactersNameAndSurname,
} from "../../utilities/common.utilities";

export type InputInfoStatus = {
  value: string;
  valid: boolean;
  showError: boolean;
};

export function handleNameChange(
  nameValue: string,
  currentSurname: string
): { info: InputInfoStatus; validTotChars: boolean } {
nameValue = nameValue ? nameValue.replace(/[0-9]/g, "") : "";
nameValue = capitalizeFormat(nameValue);
  nameValue = clearStringData(nameValue, false);

  const info: InputInfoStatus = {
    value: nameValue,
    valid: checkValidationRequiredString(nameValue, Enum_ValidationString.Any),
    showError: false,
  };

  const validTotChars = checkNumCharactersNameAndSurname(nameValue, currentSurname);
  return { info, validTotChars };
}

export function handleNameBlur(name: InputInfoStatus): InputInfoStatus {
  const value = clearStringData(name.value, true);
  return {
    ...name,
    value,
    valid: checkValidationRequiredString(value, Enum_ValidationString.Any),
    showError: true,
  };
}

export function handleSurnameChange(
  surnameValue: string,
  currentName: string
): { info: InputInfoStatus; validTotChars: boolean } {
surnameValue = surnameValue ? surnameValue.replace(/[0-9]/g, "") : "";
surnameValue = capitalizeFormat(surnameValue);
  surnameValue = clearStringData(surnameValue, false);

  const info: InputInfoStatus = {
    value: surnameValue,
    valid: checkValidationRequiredString(surnameValue, Enum_ValidationString.Any),
    showError: false,
  };

  const validTotChars = checkNumCharactersNameAndSurname(currentName, surnameValue);
  return { info, validTotChars };
}

export function handleSurnameBlur(surname: InputInfoStatus): InputInfoStatus {
  const value = clearStringData(surname.value, true);
  return {
    ...surname,
    value,
    valid: checkValidationRequiredString(value, Enum_ValidationString.Any),
    showError: true,
  };
}

export function handleNationalIdChange(
  nationalId: string,
  current: InputInfoStatus
): InputInfoStatus {
  const value = nationalId ? nationalId.toUpperCase().replaceAll(" ", "") : "";
  return {
    value,
    valid: checkValidationRequiredString(value, Enum_ValidationString.NationalIdNumber),
    showError: current.showError,
  };
}

export function handleNationalIdBlur(current: InputInfoStatus): InputInfoStatus {
  return {
    ...current,
    valid: checkValidationRequiredString(current.value, Enum_ValidationString.NationalIdNumber),
    showError: true,
  };
}

export function handleEmailChange(email: string, current: InputInfoStatus): InputInfoStatus {
  const value = email ? email.toLowerCase().replaceAll(" ", "") : "";
  return {
    value,
    valid: checkValidationRequiredString(value, Enum_ValidationString.Email),
    showError: current.showError,
  };
}

export function handleEmailBlur(current: InputInfoStatus): InputInfoStatus {
  return {
    ...current,
    valid: checkValidationRequiredString(current.value, Enum_ValidationString.Email),
    showError: true,
  };
}

export function handlePhoneChange(phone: string | null, current: InputInfoStatus): InputInfoStatus {
  const value = phone ? phone.replaceAll(" ", "") : "";
  return {
    value,
    valid: checkValidationRequiredString(value, Enum_ValidationString.NumberCode),
    showError: current.showError,
  };
}

export function handlePhoneBlur(current: InputInfoStatus): InputInfoStatus {
  return {
    ...current,
    valid: checkValidationRequiredString(current.value, Enum_ValidationString.NumberCode),
    showError: true,
  };
}

export function getErrorEmail(email: InputInfoStatus): string {
  if (!email.valid) {
    return email.value.length === 0
      ? "Campo obbligatorio"
      : "Indirizzo email non valido";
  }
  return "";
}

export function getErrorCodiceFiscale(nationalIdNumber: InputInfoStatus): string {
  if (!nationalIdNumber.valid) {
    return nationalIdNumber.value.length === 0
      ? "Campo obbligatorio"
      : "Codice fiscale non valido";
  }
  return "";
}

export const formatCouponDate = (value?: string | null): string => {
  if (!value) return "";

  const onlyDate = value.split("T")[0];
  const parts = onlyDate.split("-");

  if (parts.length !== 3) return value;

  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

export const getGenderFromCf = (cf?: string): "M" | "F" | null => {
  if (!cf || cf.length < 11) return null;

  const dayPart = cf.substring(9, 11);
  const dayCode = Number(dayPart);

  if (Number.isNaN(dayCode)) return null;
  if (dayCode >= 1 && dayCode <= 31) return "M";
  if (dayCode >= 41 && dayCode <= 71) return "F";

  return null;
};

export const maskEmail = (email: string | undefined | null): string => {
  if (!email || !email.includes('@')) return '';
  
  const [localPart, domain] = email.split('@');
  const len = localPart.length;

  // 1. Se ha 3 caratteri o meno prima della @: non facciamo vedere niente
  if (len <= 3) {
    return `${'*'.repeat(len)}@${domain}`;
  }

  // 2. Se la lunghezza è superiore a 6 caratteri: prime 2 e ultime 2 visibili
  if (len > 6) {
    const firstTwo = localPart.slice(0, 2);
    const lastTwo = localPart.slice(-2);
    const maskedCenter = '*'.repeat(len - 4);
    return `${firstTwo}${maskedCenter}${lastTwo}@${domain}`;
  }

  // 3. Negli altri casi (lunghezza tra 4 e 6 caratteri): facciamo vedere solo le ultime 3
  const lastThree = localPart.slice(-3);
  const maskedStart = '*'.repeat(len - 3);
  return `${maskedStart}${lastThree}@${domain}`;
};

export function getFirstInvalidFieldKey(fields: {
  name: InputInfoStatus;
  surname: InputInfoStatus;
  nationalIdNumber: InputInfoStatus;
  email: InputInfoStatus;
  phone: InputInfoStatus;
}): "name" | "surname" | "nationalIdNumber" | "email" | "phone" | null {
  const order: (keyof typeof fields)[] = [
    "name",
    "surname",
    "nationalIdNumber",
    "email",
    "phone",
  ];

  const firstInvalid = order.find((key) => !fields[key].valid);
  return firstInvalid ?? null;
}